// The return trip. Clicking a checklist item drops you on the page where the
// work happens — this bar is what tells you why you're there and how to get
// back, so a deep link isn't a one-way door.
//
// It deliberately does NOT lock navigation. Someone setting up a store needs to
// wander off mid-task and look at something, and trapping them in a flow is the
// thing people resent most about onboarding. Instead the active task lives in
// sessionStorage (the same trick SetupContext uses for wizard runs), so the bar
// follows you across every page until you finish or dismiss it. Leaving is
// cheap; coming back is free.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Check, X, Sparkles, ArrowLeft } from 'lucide-react';
import { SETUP_TASKS_BY_ID, sectionOfTask } from '@/lib/setupChecklist';
import { SETUP_MODULES_BY_KEY } from '@/lib/setupModules';
import { setupChecklistService } from '@/services/setupChecklistService';
import { setupService } from '@/services/setupService';
import { scrollAppToTop } from '@/lib/scrollAppToTop';
import SpotlightTour from './SpotlightTour';

export const SETUP_TASK_PARAM = 'setupTask';
const ACTIVE_TASK_KEY = 'ldg-setup-task';
/** The checklist itself — being here IS being back, so the bar stands down. */
const CHECKLIST_PATH = '/setup';

const readStoredTask = (): string | null => {
  try {
    const id = sessionStorage.getItem(ACTIVE_TASK_KEY);
    return id && SETUP_TASKS_BY_ID[id] ? id : null;
  } catch { return null; }
};

const clearStoredTask = () => {
  try { sessionStorage.removeItem(ACTIVE_TASK_KEY); } catch { /* noop */ }
};

/** Attach the task marker to a route that may already carry a query string. */
export function routeForTask(route: string, taskId: string): string {
  if (!route || route === '—') return `${CHECKLIST_PATH}?${SETUP_TASK_PARAM}=${encodeURIComponent(taskId)}`;
  const joiner = route.includes('?') ? '&' : '?';
  return `${route}${joiner}${SETUP_TASK_PARAM}=${encodeURIComponent(taskId)}`;
}

export default function SetupTaskBar() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tourOpen, setTourOpen] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState<string | null>(readStoredTask);

  const paramTaskId = searchParams.get(SETUP_TASK_PARAM);
  const onChecklist = location.pathname === CHECKLIST_PATH;

  // Arriving with the marker starts (or switches) the trip and pins it for the
  // rest of the session, so the bar survives navigating away from this page.
  useEffect(() => {
    if (!paramTaskId || !SETUP_TASKS_BY_ID[paramTaskId]) return;
    try { sessionStorage.setItem(ACTIVE_TASK_KEY, paramTaskId); } catch { /* noop */ }
    setActiveTaskId(paramTaskId);
  }, [paramTaskId]);

  // Back on the checklist — the trip is over whether they finished it or not.
  useEffect(() => {
    if (!onChecklist) return;
    clearStoredTask();
    setActiveTaskId(null);
  }, [onChecklist]);

  const taskId = activeTaskId;
  const task = taskId ? SETUP_TASKS_BY_ID[taskId] : undefined;
  const section = taskId ? sectionOfTask(taskId) : undefined;

  // Tour steps for whatever page we've landed on. A task's own walkthrough wins:
  // it exists precisely because the module tour surveys the page instead of
  // walking the one flow this task is about. Empty on unrelated pages, which is
  // how the "Show me around" button knows to stay hidden.
  const tourSteps = useMemo(() => {
    if (!task) return [];
    const taskRoute = task.route.split('?')[0];
    if (task.tourSteps?.length && taskRoute === location.pathname) return task.tourSteps;
    if (!section?.module) return [];
    const stops = SETUP_MODULES_BY_KEY[section.module]?.stops ?? [];
    return stops.find((s) => s.route.split('?')[0] === location.pathname)?.tourSteps ?? [];
  }, [task, section, location.pathname]);

  // A 'learn' task with its own walkthrough should just start walking — the user
  // already asked for it by clicking "Show me". Once per session per task, and
  // only on the page the steps actually belong to.
  //
  // The wait matters. Opening the instant this mounts raced the page's own data
  // fetch: SpotlightTour polled for anchors that did not exist yet, gave up, and
  // closed — but the once-per-session flag had already been spent, so the
  // walkthrough could never play again. It looked exactly like a dead button.
  // So: poll for the first anchor ourselves, and only burn the flag once the
  // page is genuinely ready to be toured.
  useEffect(() => {
    if (!taskId || task?.kind !== 'learn' || !tourSteps.length) return;
    const seen = `ldg-task-tour-${taskId}`;
    if (sessionStorage.getItem(seen)) return;

    let tries = 0;
    let timer = 0;
    const attempt = () => {
      const ready = tourSteps.some((s) => document.querySelector(`[data-tour="${s.anchor}"]`));
      if (ready) {
        sessionStorage.setItem(seen, '1');
        setupService.trackEvent('task_tour_autoplay', section?.id, { taskId });
        setTourOpen(true);
        return;
      }
      // ~6s of grace, then give up WITHOUT spending the flag — "Show me around"
      // in the bar is still there, and a later visit gets another shot.
      if (++tries < 30) timer = window.setTimeout(attempt, 200);
    };
    attempt();
    return () => window.clearTimeout(timer);
  }, [taskId, task, tourSteps, section]);

  const endTrip = () => {
    clearStoredTask();
    setActiveTaskId(null);
  };

  const backToChecklist = () => {
    endTrip();
    navigate(section ? `${CHECKLIST_PATH}?section=${section.id}` : CHECKLIST_PATH);
    scrollAppToTop();
  };

  const doneMutation = useMutation({
    mutationFn: () => setupChecklistService.setTask(taskId!, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['setup-checklist'] });
      setupService.trackEvent('checklist_task_done', section?.id, { taskId });
      toast.success('Nice — ticked off your list.');
      backToChecklist();
    },
    onError: () => toast.error("Couldn't save that — please try again.")
  });

  if (!task || !taskId || onChecklist) return null;

  const onTaskPage = task.route.split('?')[0] === location.pathname;

  return (
    <>
      {/* data-setup-bar is load-bearing: SpotlightTour measures it to keep the
          highlight from being drawn underneath this bar. Dropping it made tours
          spotlight things hidden behind the header. */}
      <div data-setup-bar className="sticky top-0 min-[938px]:top-[calc(4rem+env(safe-area-inset-top))] z-40 border-b-2 border-[#E51636] bg-white px-4 py-3 shadow-sm">
        <div className="mx-auto flex max-w-6xl flex-col gap-2 md:flex-row md:items-center md:gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-[#E51636]">
              Store setup{section ? ` · ${section.label}` : ''}
            </p>
            <p className="truncate text-sm font-medium text-gray-800">{task.label}</p>
            {/* Wandered off mid-task: say so plainly rather than looking broken. */}
            {!onTaskPage && (
              <p className="truncate text-xs text-gray-500">
                You’re away from this step — pick it back up any time.
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="ghost" className="text-gray-500" onClick={backToChecklist}>
              <ArrowLeft className="mr-1 h-4 w-4" /> Checklist
            </Button>
            {tourSteps.length > 0 && (
              <Button
                size="sm"
                variant="ghost"
                // Neutral, not brand red: only one thing on this bar should look
                // like the action to take, and it isn't this one.
                className="text-gray-600"
                onClick={() => {
                  setupService.trackEvent('tour_opened', section?.module, { taskId });
                  setTourOpen(true);
                }}
              >
                <Sparkles className="mr-1 h-4 w-4" /> Show me around
              </Button>
            )}
            {/* Explicit brand fill rather than the Button default: `bg-primary`
                rendered near-white here, so the one action that finishes the
                step looked like a third link beside Checklist and Dismiss. */}
            {onTaskPage ? (
              <Button
                className="ldg-pulse-cta h-11 rounded-xl bg-[#E51636] px-6 text-[15px] font-bold text-white shadow-md shadow-[#E51636]/30 hover:bg-[#c91230]"
                onClick={() => doneMutation.mutate()}
                disabled={doneMutation.isPending}
              >
                <Check className="mr-1.5 h-5 w-5" />
                {doneMutation.isPending ? 'Saving…' : 'Mark done'}
              </Button>
            ) : (
              <Button
                className="ldg-pulse-cta h-11 rounded-xl bg-[#E51636] px-6 text-[15px] font-bold text-white shadow-md shadow-[#E51636]/30 hover:bg-[#c91230]"
                onClick={() => navigate(routeForTask(task.route, taskId))}
              >
                Back to this step
              </Button>
            )}
            <button
              className="ml-1 flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
              onClick={endTrip}
              title="Hide this until you pick another step"
            >
              <X className="h-3 w-3" /> Dismiss
            </button>
          </div>
        </div>
      </div>

      {tourOpen && tourSteps.length > 0 && (
        <SpotlightTour
          steps={tourSteps}
          onClose={() => { setTourOpen(false); scrollAppToTop(true); }}
          onEnd={(completed) =>
            setupService.trackEvent(completed ? 'tour_completed' : 'tour_skipped', section?.module, { taskId })
          }
        />
      )}
    </>
  );
}
